/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { CSSProperties, FC, useMemo, useState } from 'react';
import { Button, Rate, Select, Tag, Tooltip, message } from 'antd';
import {
  BankOutlined,
  DownOutlined,
  DownloadOutlined,
  MailOutlined,
  RightOutlined,
  StarOutlined,
  StarFilled,
  UserOutlined,
  CrownOutlined,
} from '@ant-design/icons';
import { useIntl } from 'react-intl';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { OffersEvaluationExpanded } from './OffersEvaluationExpanded';
import {
  buildMergedEvaluationVouchers,
  EVALUATION_OPTIONS,
  EvaluationOfferLike,
  EvaluationValue,
  EvaluationVoucherLike,
  EvaluationVoucherResponseLike,
  getEvaluationOption,
  getOfferVendorCompany,
  getOfferVendorEmail,
  getOfferVendorName,
} from './evaluationData';
import { downloadOfferZip } from '~src/utils/downloader/downloadOfferZip';
import { OfferReviewDrawer } from '~components/popups-modals/Review/OfferReviewDrawer';

type OffersEvaluationRowProps = {
  projectId: number;
  tenderId: number;
  contractId: number;
  contractName?: string;
  offer: EvaluationOfferLike;
  serial: string;
  vouchers: EvaluationVoucherLike[];
  responses?: EvaluationVoucherResponseLike[];
  evaluationValue: EvaluationValue;
  onEvaluationChange: (offerId: number, value: EvaluationValue) => void;
  isEvaluationLocked?: boolean;
};

const getEvaluationOptionDropdownStyle = (
  value: EvaluationValue
): CSSProperties => {
  if (value === 1 || value === 3 || value === 4) {
    return {
      borderTop: '1px solid #f0f0f0',
      marginTop: 6,
      paddingTop: 10,
    };
  }

  return {};
};

const OffersEvaluationRowBase: FC<OffersEvaluationRowProps> = ({
  projectId,
  tenderId,
  contractId,
  contractName,
  offer,
  serial,
  vouchers,
  responses = [],
  evaluationValue,
  onEvaluationChange,
  isEvaluationLocked,
}) => {
  const styles = useStyles(styleFn);
  const intl = useIntl();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isZipDownloading, setIsZipDownloading] = useState(false);
  const [reviewDrawerVisible, setReviewDrawerVisible] = useState(false);

  const mergedVouchers = useMemo(
    () => buildMergedEvaluationVouchers(vouchers, responses, intl),
    [vouchers, responses, intl]
  );

  const vendorName = getOfferVendorName(offer, intl);
  const vendorCompany = getOfferVendorCompany(offer);
  const vendorEmail = getOfferVendorEmail(offer);

  const reviewDashboard = offer.evaluation?.evaluation_star_review_dashboard;
  const hasReviews = reviewDashboard && reviewDashboard.total_reviews > 0;
  const avgRating = reviewDashboard?.avg_star_rating ?? 0;
  const totalReviews = reviewDashboard?.total_reviews ?? 0;

  const handleZipDownload = async () => {
    setIsZipDownloading(true);

    // Activate the download progress modal
    const { useStore } = await import('~src/store');
    const { DownloadingState } = await import('~src/store');
    useStore.getState().setIsOfferDownload(true);
    useStore.getState().changeDownloadingActive(true);
    useStore.getState().changeDownloadingState(DownloadingState.PREPARING);

    try {
      await downloadOfferZip({
        projectId,
        tenderId,
        contractId,
        offerId: offer.id,
        contractName: contractName || `Contract_${contractId}`,
        offerName: `${serial} ${vendorName}`,
        mergedVouchers,
        offerSerial: serial,
      });
    } catch {
      message.error(intl.formatMessage({ id: 'attachment-download-failed' }));
    } finally {
      setIsZipDownloading(false);
      // Modal will auto-close after FINISHING state (handled by saveFilesToZip)
    }
  };

  const selectedOption = getEvaluationOption(evaluationValue);
  const isPriority = evaluationValue === 5;

  return (
    <div css={styles.wrapper}>
      <div css={styles.row}>
        <button
          type="button"
          css={styles.expandButton}
          onClick={() => setIsExpanded((prev) => !prev)}>
          {isExpanded ? <DownOutlined /> : <RightOutlined />}
        </button>

        <div css={styles.vendorCell}>
          <div css={styles.vendorTitleRow}>
            <UserOutlined css={styles.vendorIcon} />
            <span css={styles.vendorName}>
              {vendorName} <span css={styles.offerSerial}>{serial}</span>
            </span>
          </div>

          <div css={styles.vendorMetaRow}>
            <span css={styles.metaItem}>
              <BankOutlined />
              <span>{vendorCompany}</span>
            </span>

            <span css={styles.metaItem}>
              <MailOutlined />
              <span>{vendorEmail}</span>
            </span>
          </div>
        </div>

        <div css={styles.evaluationCell}>
          <div css={styles.cellLabel}>
            {intl.formatMessage({
              id: 'evaluation-row-evaluation-label',
            })}
          </div>

          <Tooltip
            title={
              isEvaluationLocked
                ? intl.formatMessage({
                    id: 'evaluation-row-locked-tooltip',
                  })
                : undefined
            }>
            <span>
              <Select
                size="small"
                value={evaluationValue}
                disabled={!!isEvaluationLocked}
                onChange={(value) =>
                  onEvaluationChange(offer.id, Number(value) as EvaluationValue)
                }
                css={styles.evaluationSelect}
                dropdownMatchSelectWidth={false}
                dropdownStyle={{
                  padding: '6px',
                  borderRadius: 6,
                }}
                listHeight={260}>
                {EVALUATION_OPTIONS.map((option) => (
                  <Select.Option
                    key={option.value}
                    value={option.value}
                    style={getEvaluationOptionDropdownStyle(option.value)}>
                    <span css={styles.optionRow}>
                      <span
                        css={[
                          styles.optionDot,
                          { backgroundColor: option.color },
                        ]}
                      />
                      <span css={styles.optionLabel}>
                        {intl.formatMessage({
                          id: option.intlId,
                        })}
                      </span>
                    </span>
                  </Select.Option>
                ))}
              </Select>
            </span>
          </Tooltip>
        </div>

        <div css={styles.statusPreviewCell}>
          <div css={styles.cellLabel}>
            {intl.formatMessage({
              id: 'evaluation-row-current-status-label',
            })}
          </div>

          <Tag
            css={[
              styles.statusTag,
              {
                color: selectedOption.color,
                borderColor: selectedOption.color,
                background: selectedOption.background,
              },
            ]}>
            {isPriority && <CrownOutlined css={styles.priorityIcon} />}
            {intl.formatMessage({
              id: selectedOption.intlId,
            })}
          </Tag>
        </div>

        <div css={styles.reviewCell}>
          <div css={styles.cellLabel}>
            {/* {intl.formatMessage({
              id: 'evaluation-row-review-label',
            })} */}
          </div>

          <div
            css={styles.reviewDisplay}
            onClick={() => setReviewDrawerVisible(true)}
            role="button"
            tabIndex={0}
            onKeyPress={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                setReviewDrawerVisible(true);
              }
            }}>
            {hasReviews ? (
              <div css={styles.reviewStats}>
                <span css={styles.avgRatingNumber}>{avgRating.toFixed(1)}</span>
                <div css={styles.starRating}>
                  <Rate disabled allowHalf value={avgRating} />
                </div>
                <span css={styles.reviewCount}>({totalReviews})</span>
              </div>
            ) : (
              <div css={styles.reviewEmpty}>
                <StarOutlined />
                <span>
                  {intl.formatMessage({ id: 'review-no-reviews-row' })}
                </span>
                <span css={styles.reviewEmptyCount}>(0)</span>
              </div>
            )}
          </div>
        </div>

        <div css={styles.actionCell}>
          <Button
            size="small"
            css={styles.detailsButton}
            onClick={() => setIsExpanded((prev) => !prev)}>
            {isExpanded
              ? intl.formatMessage({
                  id: 'evaluation-row-hide-details',
                })
              : intl.formatMessage({
                  id: 'evaluation-row-view-details',
                })}
          </Button>

          <Tooltip
            title={intl.formatMessage({
              id: 'evaluation-row-download-zip',
            })}>
            <Button
              size="small"
              icon={<DownloadOutlined />}
              loading={isZipDownloading}
              onClick={handleZipDownload}
              css={styles.zipButton}
              aria-label={intl.formatMessage({
                id: 'evaluation-row-download-all',
              })}
            />
          </Tooltip>
        </div>
      </div>

      {isExpanded && (
        <div css={styles.expandedContent}>
          <OffersEvaluationExpanded
            projectId={projectId}
            tenderId={tenderId}
            contractId={contractId}
            offerId={offer.id}
            vouchers={mergedVouchers}
          />
        </div>
      )}

      <OfferReviewDrawer
        visible={reviewDrawerVisible}
        onClose={() => setReviewDrawerVisible(false)}
        projectId={projectId}
        tenderId={tenderId}
        contractId={contractId}
        offerId={offer.id}
        phase="evaluation"
        offerName={`${serial} ${vendorName}`}
        isReadOnly={isEvaluationLocked}
      />
    </div>
  );
};

// Memoized so a single offer's review/dashboard update doesn't re-render every
// other row in the list. Requires stable props from the parent view.
export const OffersEvaluationRow = React.memo(OffersEvaluationRowBase);

const styleFn: ThemedStyleFn = () => ({
  wrapper: {
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    border: '1px solid #e8e8e8',
    borderRadius: 6,
    background: '#ffffff',
    overflow: 'hidden',
    boxSizing: 'border-box',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)',
  },

  row: {
    display: 'grid',
    gridTemplateColumns:
      '24px minmax(300px, 1.45fr) repeat(3, minmax(160px, 0.75fr)) 172px',
    alignItems: 'center',
    gap: 16,
    minHeight: 86,
    padding: '14px 16px',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    boxSizing: 'border-box',

    '&:hover': {
      background: '#fafafa',
    },
  },

  expandButton: {
    width: 24,
    height: 24,
    padding: 0,
    border: 'none',
    background: 'transparent',
    color: '#595959',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,

    '&:hover': {
      color: '#1890ff',
    },
  },

  vendorCell: {
    minWidth: 0,
    overflow: 'hidden',
  },

  vendorTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    minWidth: 0,
    overflow: 'hidden',
  },

  vendorIcon: {
    color: '#1890ff',
    flexShrink: 0,
  },

  vendorName: {
    fontWeight: 700,
    color: '#222222',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  offerSerial: {
    color: '#8c8c8c',
    fontWeight: 400,
    marginRight: 8,
    fontSize: '10px',
  },

  vendorMetaRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '6px 14px',
    marginTop: 7,
    minWidth: 0,
    maxWidth: '100%',
    overflow: 'hidden',
    color: '#666666',
    fontSize: 12,
  },

  metaItem: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    minWidth: 0,
    overflow: 'hidden',

    span: {
      minWidth: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  },

  evaluationCell: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    height: 49,
    minWidth: 0,
    width: '100%',
    overflow: 'hidden',

    '> span': {
      display: 'block',
      width: '100%',
      height: 32,
    },
  },

  statusPreviewCell: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    height: 49,
    minWidth: 0,
    width: '100%',
    overflow: 'hidden',
  },

  reviewCell: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    height: 49,
    minWidth: 0,
    width: '100%',
    overflow: 'hidden',
  },

  reviewDisplay: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 32,
    padding: '0 8px',
    cursor: 'pointer',
    borderRadius: 4,
    border: '1px solid #d3adf7',
    transition: 'all 0.2s',

    '&:hover': {
      backgroundColor: '#f9f0ff',
      borderColor: '#b37feb',
    },
  },

  reviewStats: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 13,
    fontWeight: 500,
  },

  starRating: {
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: 14,
    lineHeight: 1,
    margin: '-2px 0 0 0',

    '.ant-rate': {
      lineHeight: 1,
      display: 'flex',
      alignItems: 'center',
    },

    '.ant-rate-star': {
      marginRight: 2,
      fontSize: 14,
      lineHeight: 1,
    },
  },

  reviewCount: {
    color: '#595959',
    fontSize: 12,
  },

  reviewEmpty: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    color: '#8c8c8c',
    fontSize: 12,

    '.anticon': {
      fontSize: 14,
    },
  },

  reviewEmptyCount: {
    marginLeft: -2,
  },

  avgRatingNumber: {
    fontSize: 14,
    fontWeight: 500,
    color: '#262626',
  },

  reviewDivider: {
    color: '#d9d9d9',
    margin: '0 4px',
    fontWeight: 400,
  },

  cellLabel: {
    height: 11,
    color: '#999999',
    fontSize: 11,
    fontWeight: 700,
    lineHeight: '11px',
    marginBottom: 6,
    textTransform: 'uppercase',
    letterSpacing: 0.3,
    whiteSpace: 'nowrap',
  },

  evaluationSelect: {
    width: '100%',
    minWidth: 0,
    height: 32,

    '.ant-select-selector': {
      height: '32px !important',
      minHeight: '32px !important',
      padding: '0 11px !important',
      display: 'flex',
      alignItems: 'center',
      boxSizing: 'border-box',
    },

    '.ant-select-selection-item': {
      display: 'flex',
      alignItems: 'center',
      height: 30,
      lineHeight: '30px !important',
      paddingRight: 18,
    },

    '.ant-select-selection-search-input': {
      height: '30px !important',
    },

    '.ant-select-arrow': {
      top: '50%',
      marginTop: 0,
      transform: 'translateY(-50%)',
    },
  },

  optionRow: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    minWidth: 150,
    height: 24,
  },

  optionDot: {
    display: 'inline-block',
    width: 8,
    height: 8,
    borderRadius: '50%',
    flexShrink: 0,
  },

  optionLabel: {
    fontSize: 14,
    fontWeight: 500,
    color: '#222222',
    lineHeight: '22px',
  },

  statusTag: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 'fit-content',
    maxWidth: '100%',
    height: 32,
    minHeight: 32,
    margin: 0,
    padding: '0 12px',
    lineHeight: '30px',
    fontWeight: 700,
    boxSizing: 'border-box',

    '.anticon': {
      display: 'inline-flex',
      alignItems: 'center',
    },
  },

  priorityIcon: {
    marginRight: 5,
    fontSize: 12,
  },

  reviewButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 128,
    maxWidth: '100%',
    height: 32,
    minHeight: 32,
    padding: '0 14px',
    lineHeight: '30px',
    color: '#722ed1',
    borderColor: '#d3adf7',
    background: '#ffffff',
    boxSizing: 'border-box',
    boxShadow: 'none',

    '&:hover, &:focus, &:active': {
      height: 32,
      minHeight: 32,
      color: '#531dab',
      borderColor: '#9254de',
      background: '#ffffff',
      boxShadow: 'none',
    },

    '.anticon': {
      display: 'inline-flex',
      alignItems: 'center',
      color: '#faad14',
    },

    span: {
      lineHeight: '30px',
    },
  },

  actionCell: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'flex-end',
    gap: 8,
    height: 49,
    minWidth: 0,
    width: '100%',
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
  },

  disabledActionWrapper: {
    display: 'inline-flex',
    alignItems: 'center',
    flexShrink: 0,
    height: 32,
  },

  zipButton: {
    width: 32,
    minWidth: 32,
    height: 32,
    minHeight: 32,
    padding: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 4,
    lineHeight: '30px',
    boxSizing: 'border-box',
    boxShadow: 'none',

    '&:hover, &:focus, &:active': {
      width: 32,
      minWidth: 32,
      height: 32,
      minHeight: 32,
      boxShadow: 'none',
    },

    '&[disabled], &[disabled]:hover, &[disabled]:focus, &[disabled]:active': {
      width: 32,
      minWidth: 32,
      height: 32,
      minHeight: 32,
      boxShadow: 'none',
    },

    '.anticon': {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: 0,
    },
  },

  detailsButton: {
    width: 132,
    minWidth: 132,
    height: 32,
    minHeight: 32,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 14px',
    lineHeight: '30px',
    boxSizing: 'border-box',
    boxShadow: 'none',

    '&:hover, &:focus, &:active': {
      width: 132,
      minWidth: 132,
      height: 32,
      minHeight: 32,
      boxShadow: 'none',
    },

    span: {
      lineHeight: '30px',
    },
  },

  expandedContent: {
    padding: '0 16px 16px 16px',
    background: '#fcfcfc',
  },
});
